/**
 * Single source of truth for all environment-driven configuration:
 *   • Application metadata
 *   • API base URL, version, services, client headers, timeouts
 *   • Contact / messaging (XMPP) endpoints
 *   • Dev-only auth overrides
 *
 * Add any new VITE_* variable here so the rest of the codebase imports a
 * typed value instead of reading `import.meta.env.*` directly.
 */

const raw = import.meta.env;

function str(value: unknown, fallback = ''): string {
  return typeof value === 'string' && value.length > 0 ? value : fallback;
}

function num(value: unknown, fallback: number): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function bool(value: unknown, fallback: boolean): boolean {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return fallback;
}

// ---------------------------------------------------------------------------
// API configuration
// ---------------------------------------------------------------------------

/** MyU backend service names (first path segment after the API base URL). */
export const services = {
  user: str(raw.VITE_SERVICE_USER, 'userservice'),
  media: str(raw.VITE_SERVICE_MEDIA, 'mediaservice'),
  midl: str(raw.VITE_SERVICE_MIDL, 'midl'),
  notification: str(raw.VITE_SERVICE_NOTIFICATION, 'notification'),
  message: str(raw.VITE_SERVICE_MESSAGE, 'messageservice'),
  billing: str(raw.VITE_SERVICE_BILLING, 'billing'),
} as const;

export type ServiceName = keyof typeof services;

export const api = {
  /** Base URL (e.g. `https://beta-newapi.myu.co` or `/api` for dev proxy). */
  baseUrl: str(raw.VITE_API_BASE_URL, '/api'),
  /** Optional dev-only proxy target used by vite.config.ts. */
  proxyTarget: str(raw.VITE_API_PROXY_TARGET, 'https://beta-newapi.myu.co'),
  /** Request timeout in milliseconds. */
  timeout: num(raw.VITE_API_TIMEOUT, 15_000),
  /** Default API version segment (e.g. `v3`). */
  version: str(raw.VITE_API_VERSION, 'v3'),
  /** MyU client identity headers sent on every request. */
  headers: {
    appClientKey: str(raw.VITE_APP_CLIENT_KEY, 'HIRt4zoSCWeboiNa0vA7aw=='),
    appVersion: str(raw.VITE_APP_VERSION, '5.0.0'),
    deviceType: str(raw.VITE_DEVICE_TYPE, 'webapp'),
  },
  /** When true, auth endpoints resolve locally until the backend is ready. */
  useMockAuth: bool(raw.VITE_USE_MOCK_AUTH, true),
  services,
} as const;

/** Public admissions endpoints are served by the Django CMS/backoffice API. */
export const cmsApi = {
  baseUrl: str(raw.VITE_CMS_API_BASE_URL, 'https://beta-cms.myu.co/api/v1/'),
} as const;

/**
 * Build a service path like `/userservice/api/v3/user/login`.
 *
 * @example
 *   servicePath('user', 'user/login');                   // → /userservice/api/v3/user/login
 *   servicePath('media', 'upload', { version: 'v2' });   // → /mediaservice/api/v2/upload
 *   servicePath('midl', 'feed', { version: null });      // → /midl/feed   (no /api/<version>)
 */
export function servicePath(
  service: ServiceName,
  endpoint: string,
  options: { version?: string | null } = {},
): string {
  const version = options.version === undefined ? api.version : options.version;
  const cleanEndpoint = endpoint.replace(/^\/+/, '');
  const versionSegment = version ? `/api/${version}` : '';
  return `/${services[service]}${versionSegment}/${cleanEndpoint}`;
}

// ---------------------------------------------------------------------------
// Contact / messaging (XMPP chat) configuration
// ---------------------------------------------------------------------------

export const contact = {
  /** WebSocket URL for the XMPP service. */
  xmppServiceUrl: str(raw.VITE_XMPP_SERVICE_URL, 'wss://qa-chat.myu.co/ws'),
  /** XMPP server domain (JID host). */
  xmppDomain: str(raw.VITE_XMPP_DOMAIN, 'localhost'),
  /** XMPP Multi-User Chat sub-domain. */
  xmppMucDomain: str(raw.VITE_XMPP_MUC_DOMAIN, 'conference.localhost'),
} as const;

// ---------------------------------------------------------------------------
// Google OAuth configuration
// ---------------------------------------------------------------------------

export const google = {
  /** Google OAuth 2.0 Client ID for GIS (Google Identity Services). */
  clientId: str(raw.VITE_GOOGLE_CLIENT_ID, ''),
} as const;

// ---------------------------------------------------------------------------
// Dev-only overrides
// ---------------------------------------------------------------------------

export const devAuth = {
  token: str(raw.VITE_DEV_AUTH_TOKEN, ''),
  userId: str(raw.VITE_DEV_USER_ID, ''),
} as const;

// ---------------------------------------------------------------------------
// Aggregate `env` export — keep API surface backwards-compatible.
// ---------------------------------------------------------------------------

export const env = {
  appName: str(raw.VITE_APP_NAME, 'MyU Web'),
  appEnv: str(raw.VITE_APP_ENV, 'development'),

  // API (flattened for backwards compatibility with existing imports)
  apiBaseUrl: api.baseUrl,
  apiTimeout: api.timeout,
  apiVersion: api.version,
  useMockAuth: api.useMockAuth,
  appClientKey: api.headers.appClientKey,
  appVersion: api.headers.appVersion,
  deviceType: api.headers.deviceType,
  services,

  // Grouped views
  api,
  contact,
  devAuth,
  google,
  cmsApi,

  isDev: raw.DEV,
  isProd: raw.PROD,
} as const;
